import { useState, useRef, FormEvent } from 'react';
import { format } from 'date-fns';
import { STORAGE_KEY, YOUR_GOOGLE_APPS_SCRIPT_URL, PAYMENT_REQUIRED_SESSIONS } from '../constants/event.ts';

interface RSVPData {
  name: string;
  timestamp: string;
  phone: string;
  email: string;
  session: string;
  pax?: string;
  receiptFile?: File | null;
  receiptFileName?: string;
}

interface UseRSVPFormReturn {
  wishesData: RSVPData;
  setWishesData: React.Dispatch<React.SetStateAction<RSVPData>>;
  isSubmitting: boolean;
  submitError: string | null;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

export const useRSVPForm = (): UseRSVPFormReturn => {
  const [wishesData, setWishesData] = useState<RSVPData>({
    name: '',
    timestamp: '',
    phone: '',
    email: '',
    session: '',
    pax: '',
    receiptFile: null,
    receiptFileName: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    isSubmittingRef.current = true;

    // Check if payment is required for selected session
    const isPaymentRequired = PAYMENT_REQUIRED_SESSIONS.includes(wishesData.session);
    
    // Validate receipt upload for payment-required sessions
    if (isPaymentRequired && !wishesData.receiptFile) {
      setSubmitError('Payment receipt is required for this session');
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      return;
    }

    const newRsvpEntry = {
      name: wishesData.name,
      phone: wishesData.phone,
      email: wishesData.email,
      session: wishesData.session || '',
      pax: wishesData.pax || '1',
      timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      action: 'rsvp',
      receiptFileName: wishesData.receiptFileName || ''
    };

    try {
      // If there's a receipt file, convert it to base64
      let receiptBase64 = '';
      if (wishesData.receiptFile) {
        receiptBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            resolve(base64String.split(',')[1]); // Remove data:image/xxx;base64, prefix
          };
          reader.onerror = reject;
          reader.readAsDataURL(wishesData.receiptFile as File);
        });
      }

      // Send to Google Sheets with receipt data
      await fetch(
        YOUR_GOOGLE_APPS_SCRIPT_URL,
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...newRsvpEntry,
            receiptData: receiptBase64,
            receiptMimeType: wishesData.receiptFile?.type || ''
          })
        }
      );

      // Also save to localStorage as backup (without the base64 data to save space)
      const existingEntries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const updatedEntries = [...existingEntries, newRsvpEntry];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEntries));

      alert('RSVP successfully submitted!');
      
      // Reset form
      setWishesData({
        name: '',
        timestamp: '',
        phone: '',
        email: '',
        session: '',
        pax: '',
        receiptFile: null,
        receiptFileName: ''
      });
    } catch (error) {
      console.error('Error saving RSVP:', error);
      setSubmitError('Sorry, there was a problem submitting your RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return {
    wishesData,
    setWishesData,
    isSubmitting,
    submitError,
    handleSubmit
  };
};
